import { HttpClient } from "@angular/common/http";
import { DOCUMENT, inject, Injectable } from "@angular/core";
import { BehaviorSubject, firstValueFrom } from "rxjs";
import { v4 as uuid } from "uuid";
import { SessionResponse, SessionRole, SessionWorkspace } from "../types/session.types";

@Injectable({
  providedIn: "root",
})
export class BaseAuthService {
  protected document = inject(DOCUMENT);
  protected http = inject(HttpClient);
  protected REQUESTWEBAPPTOKEN_KEY = "requestwebapptoken"; // key of the uuid in sessionstorage
  protected TABINDEX_KEY = "tabindex"; // key of the uuid in sessionstorage
  protected PORTAL_URL: string | null = null; // url of the portal to authorize with
  protected API_URL: string | null = null; // url of the api to login
  protected REDIRECT_URL: string | null = null; // url for after redirect, will append to origin. for example /auth-callback will be https://localhost:4201/auth-callback
  protected VIEW: "email" | "qr" | null = null; // default view, will be given in hashtag

  public session = new BehaviorSubject<SessionResponse | null>(null);
  public ready = new BehaviorSubject<boolean>(false);
  public tabindex: string | null = null;

  public async initialize(data: { PORTAL_URL: string; API_URL: string; REDIRECT_URL?: string }, readyIfDone = true) {
    this.PORTAL_URL = data.PORTAL_URL;
    this.API_URL = data.API_URL;
    this.REDIRECT_URL = data.REDIRECT_URL ?? null;
    this.tabindex = sessionStorage.getItem(this.TABINDEX_KEY) || null;

    const result = await this.check();

    if (!this.ready.value && readyIfDone) this.ready.next(true);

    return result;
  }

  public requestToken() {
    if (!this.PORTAL_URL) throw new Error("PORTAL_URL not initialized!");

    const requestwebapptoken = uuid();
    sessionStorage.setItem(this.REQUESTWEBAPPTOKEN_KEY, requestwebapptoken);

    let url = this.document.location.origin;
    if (this.REDIRECT_URL) url += this.REDIRECT_URL;

    const query = new URLSearchParams({ url, requestwebapptoken });

    let location = `${this.PORTAL_URL}authorize?${query.toString()}`;

    if (this.VIEW) location += `#v=${this.VIEW}`;

    this.document.location = location;
  }

  public async redeemToken() {
    if (!this.API_URL) throw new Error("API_URL not initialized!");

    const params = new URLSearchParams(this.document.location.hash.substring(1));

    const ffwdrequest = params.get("code");
    const deviceid = sessionStorage.getItem(this.REQUESTWEBAPPTOKEN_KEY);

    this.document.location.hash = "";
    sessionStorage.removeItem(this.REQUESTWEBAPPTOKEN_KEY);

    if (!ffwdrequest || ffwdrequest === "null") throw new Error("Unable to authenticate: ffwdrequest is missing");
    if (!deviceid || deviceid === "null") throw new Error("Unable to authenticate: deviceid is missing");

    console.log({ ffwdrequest, deviceid });
    const response = await firstValueFrom(this.http.post(`${this.API_URL}security/login`, { ffwdrequest, deviceid }));
    const session = this.isSessionResponse(response) ? response : null;
    await this.update(session);
  }

  public async check() {
    let session: SessionResponse | null = null;
    if (this.tabindex) {
      const response = await firstValueFrom(this.http.post<SessionResponse>(`${this.API_URL}security/checkSession`, {}));
      session = this.isSessionResponse(response) ? response : null;
      await this.update(session);
    }
  }

  public async changeRole(role: SessionRole) {
    const current = (await firstValueFrom(this.session))?.role;
    try {
      const response = await firstValueFrom(
        this.http.post<{ session: SessionResponse; sto?: string }>(`${this.API_URL}security/changeRole`, {
          currentRoleID: current?.id,
          newRoleID: role.id,
          tabindex: this.tabindex,
        }),
      );

      const session = this.isSessionResponse(response.session) ? response.session : null;

      // if (this.isInvalid(response)) {
      //   const result = await this.handleSessionEvent(response);
      //   if (result) {
      //     return this.changeRole(role);
      //   }
      // }

      return session;
    } catch (err) {
      console.log(err);
    }
    return null;
  }

  public async changeWorkspace(workspace: SessionWorkspace) {
    const current = (await firstValueFrom(this.session))?.workspace;
    try {
      const response = await firstValueFrom(
        this.http.post<{ session: SessionResponse; sto?: string }>(`${this.API_URL}security/changeWorkspace`, {
          currentWorkspaceID: current?.id,
          newWorkspaceID: workspace.id,
          tabindex: this.tabindex,
        }),
      );

      const session = this.isSessionResponse(response.session) ? response.session : null;

      // if (this.isInvalid(response)) {
      //   const result = await this.handleSessionEvent(response);
      //   if (result) {
      //     return this.changeWorkspace(workspace);
      //   }
      // }

      return session;
    } catch (err) {
      console.log(err);
    }
    return null;
  }

  public async logout(): Promise<void> {
    await firstValueFrom(this.http.post(`${this.API_URL}security/logoff`, { tabindex: this.tabindex }));
    this.update(null);
  }

  protected async update(session: SessionResponse | null) {
    this.session.next(session);
    this.tabindex = session?.session.tabindex ?? null;

    if (this.tabindex === null) {
      console.log("Removing tabindex");
      sessionStorage.removeItem(this.TABINDEX_KEY);
    } else {
      console.log("Setting tabindex", this.tabindex);
      sessionStorage.setItem(this.TABINDEX_KEY, this.tabindex);
    }
  }

  private isSessionResponse(obj: unknown): obj is SessionResponse {
    return typeof obj === "object" && obj !== null && "session" in obj;
  }
}
